import React, { ReactNode } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import _IconContainer from "Banner-IconContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Banner.tsx?styleId=IconContainer";
import _MessageContainer from "Banner-MessageContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Banner.tsx?styleId=MessageContainer";
import _Container from "Banner-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Banner.tsx?styleId=Container";
import _ActionsContainer from "Banner-ActionsContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Banner.tsx?styleId=ActionsContainer";
import _ActionButton from "Banner-ActionButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Banner.tsx?styleId=ActionButton";


export type BannerAction = {
  type?: 'primary' | 'inverse';
  content: ReactNode;
  onClick?: () => void;
};

export type BannerProps = React.HTMLAttributes<HTMLDivElement> & {
  type?: 'info' | 'success' | 'warning' | 'danger';
  border?: 'all' | 'bottom';
  icon?: ReactNode;
  actions?: BannerAction[];
};

const IconContainer = /*#__PURE__*/styled("div", null, {
  displayName: "IconContainer",
  styles: _IconContainer
});

const MessageContainer = /*#__PURE__*/styled("div", null, {
  displayName: "MessageContainer",
  styles: _MessageContainer
});

const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const ActionsContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ActionsContainer",
  styles: _ActionsContainer
});

const ActionButton = /*#__PURE__*/styled(Button, null, {
  displayName: "ActionButton",
  styles: _ActionButton
});

/**
 * A banner component. This component is commonly used to display
 * contextual messages to users. It draws more attention than simple text,
 * so it should be used with care.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Banner } from 'js/design_system/Banner';
 *
 * <Banner
 *   type='info'
 *   actions={[
 *     { type: 'inverse', content: 'Cancel', onClick: () => alert('Cancel!') },
 *     { content: 'Confirm', onClick: () => alert('Confirm!') },
 *   ]}
 * >
 *   I'm an important banner! Click an action to get started.
 * </Banner>
 * ```
 */
export const Banner: React.FC<BannerProps> = ({
  type,
  border = 'all',
  icon,
  actions = [],
  children,
  ...props
}) => {
  return (
    <Container type={type} border={border} {...props}>
      <MessageContainer>
        {icon && <IconContainer>{icon}</IconContainer>}
        {children}
      </MessageContainer>

      {actions.length > 0 && (
        <ActionsContainer>
          {actions.map(
            ({ type = 'primary', content, onClick = () => null }, index) => {
              return (
                <ActionButton
                  key={index}
                  inverse={type === 'inverse'}
                  onClick={onClick}
                >
                  {content}
                </ActionButton>
              );
            },
          )}
        </ActionsContainer>
      )}
    </Container>
  );
};
